import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Hash, Download, Loader2, RefreshCw, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { hsnReportApiService, HsnReportRow } from '../services/hsnReportApi';
import { getHsnReportConfig } from '../config/uiConfig';

const HsnReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();

  const { todayDate, yesterdayDate } = useMemo(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const toDateInputValue = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      todayDate: toDateInputValue(today),
      yesterdayDate: toDateInputValue(yesterday),
    };
  }, []);

  const [rows, setRows] = useState<HsnReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState(yesterdayDate);
  const [reportDateTo, setReportDateTo] = useState(todayDate);
  const [appliedReportDateFrom, setAppliedReportDateFrom] = useState(yesterdayDate);
  const [appliedReportDateTo, setAppliedReportDateTo] = useState(todayDate);

  const loadReport = async (from = appliedReportDateFrom, to = appliedReportDateTo) => {
    if (!selectedBranch) {
      setRows([]);
      return;
    }

    try {
      setLoading(true);
      const response = await hsnReportApiService.getHsnReport(
        selectedBranch,
        from || undefined,
        to || undefined
      );
      setRows(response.success && Array.isArray(response.data) ? response.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      void loadReport();
    } else {
      setRows([]);
    }
  }, [selectedBranch]);

  const handleApplyFilters = async () => {
    if (reportDateFrom && reportDateTo && reportDateFrom > reportDateTo) {
      toast.error('Date from cannot be after date to.');
      return;
    }
    setAppliedReportDateFrom(reportDateFrom);
    setAppliedReportDateTo(reportDateTo);
    await loadReport(reportDateFrom, reportDateTo);
  };

  const handleClearFilters = async () => {
    setReportDateFrom('');
    setReportDateTo('');
    setAppliedReportDateFrom('');
    setAppliedReportDateTo('');
    await loadReport('', '');
  };

  const formatNumber = (value?: number | null) => {
    if (value == null || Number.isNaN(value)) return '0.00';
    return Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const uomDisplayByNormalized = useMemo(() => {
    const labels = getHsnReportConfig().uomLabels ?? {};
    const map = new Map<string, string>();
    for (const [key, label] of Object.entries(labels)) {
      map.set(key.trim().toLowerCase(), label);
    }
    return map;
  }, []);

  const formatUomDisplay = (uom?: string | null) => {
    const raw = (uom ?? '').trim();
    if (!raw) return '';
    return uomDisplayByNormalized.get(raw.toLowerCase()) ?? raw;
  };

  const reportSummary = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.qty += Number(row.totalBilledQuantity ?? 0);
        acc.amount += Number(row.totalAmount ?? 0);
        acc.cgst += Number(row.totalCgst ?? 0);
        acc.sgst += Number(row.totalSgst ?? 0);
        acc.igst += Number(row.totalIgst ?? 0);
        return acc;
      },
      { qty: 0, amount: 0, cgst: 0, sgst: 0, igst: 0 }
    );
  }, [rows]);

  const getRateTotal = (row: HsnReportRow) =>
    Number(row.cgstRate ?? 0) + Number(row.sgstRate ?? 0) + Number(row.igstRate ?? 0);

  const getTotalTax = (row: HsnReportRow) =>
    Number(row.totalCgst ?? 0) + Number(row.totalSgst ?? 0) + Number(row.totalIgst ?? 0);

  const getGrandTotal = (row: HsnReportRow) => Number(row.totalAmount ?? 0) + getTotalTax(row);

  const exportToCSV = () => {
    const headers = [
      'HSN Code',
      'UOM',
      'Rate',
      'Total Billed Quantity',
      'Total Amount',
      'Total CGST',
      'Total SGST',
      'Total IGST',
      'Total Tax',
      'Grand Total',
    ];

    const csvRows = rows.map((row) => [
      row.hsnCode ?? '',
      formatUomDisplay(row.uom),
      getRateTotal(row).toFixed(2),
      row.totalBilledQuantity ?? '',
      row.totalAmount ?? '',
      row.totalCgst ?? '',
      row.totalSgst ?? '',
      row.totalIgst ?? '',
      getTotalTax(row).toFixed(2),
      getGrandTotal(row).toFixed(2),
    ]);

    const csvContent = [headers, ...csvRows]
      .map((line) => line.map((field) => `"${String(field ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hsn-report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <Hash className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">HSN Report</CardTitle>
                  <p className="text-sm text-primary-100">HSN and UOM aggregates from sales</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/invoices')}
                className="border-white/30 bg-white text-primary-700 hover:bg-primary-50"
              >
                Back to Invoices
              </Button>
            </div>
          </CardHeader>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <Calendar className="h-5 w-5 text-primary-700" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="hsnReportDateFrom">Date From</Label>
                <Input
                  id="hsnReportDateFrom"
                  type="date"
                  value={reportDateFrom}
                  onChange={(e) => setReportDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hsnReportDateTo">Date To</Label>
                <Input
                  id="hsnReportDateTo"
                  type="date"
                  value={reportDateTo}
                  onChange={(e) => setReportDateTo(e.target.value)}
                />
              </div>
              <Button onClick={() => void handleApplyFilters()} className="bg-primary-600 hover:bg-primary-700 text-white">
                <RefreshCw className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
              <Button variant="outline" onClick={() => void handleClearFilters()}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total billed quantity</div>
              <div className="text-2xl font-semibold text-primary-800">{formatNumber(reportSummary.qty)}</div>
            </CardContent>
          </Card>
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total amount</div>
              <div className="text-2xl font-semibold text-primary-800">₹{formatNumber(reportSummary.amount)}</div>
            </CardContent>
          </Card>
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total CGST</div>
              <div className="text-2xl font-semibold text-primary-800">₹{formatNumber(reportSummary.cgst)}</div>
            </CardContent>
          </Card>
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total SGST</div>
              <div className="text-2xl font-semibold text-primary-800">₹{formatNumber(reportSummary.sgst)}</div>
            </CardContent>
          </Card>
          <Card className="border-primary-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total IGST</div>
              <div className="text-2xl font-semibold text-primary-800">₹{formatNumber(reportSummary.igst)}</div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-lg text-primary-800">Report data</CardTitle>
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="border-primary-200 text-primary-700 hover:bg-primary-50"
                disabled={rows.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {!selectedBranch ? (
              <div className="text-center py-8 text-gray-500">Please select a branch to view the report.</div>
            ) : loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No HSN report data found for the selected filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead>HSN code</TableHead>
                      <TableHead>UOM</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Total billed quantity</TableHead>
                      <TableHead className="text-right">Total amount</TableHead>
                      <TableHead className="text-right">Total CGST</TableHead>
                      <TableHead className="text-right">Total SGST</TableHead>
                      <TableHead className="text-right">Total IGST</TableHead>
                      <TableHead className="text-right">Total tax</TableHead>
                      <TableHead className="text-right">Grand total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow key={`${row.hsnCode}-${row.uom}-${index}`} className="hover:bg-primary-50/40">
                        <TableCell>{row.hsnCode ?? '—'}</TableCell>
                        <TableCell>{formatUomDisplay(row.uom) || '—'}</TableCell>
                        <TableCell className="text-right">{formatNumber(getRateTotal(row))}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.totalBilledQuantity)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.totalAmount)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.totalCgst)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.totalSgst)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.totalIgst)}</TableCell>
                        <TableCell className="text-right">{formatNumber(getTotalTax(row))}</TableCell>
                        <TableCell className="text-right">{formatNumber(getGrandTotal(row))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HsnReportPage;
